import isFunction from 'lodash/isFunction';
import {FetchServer} from 'domsuite';
import sinon from 'sinon';

let _fetchServer;

function _sortHandlers() {
  const newFetchServerHandlers = {};
  const sortedHandlersArr = Object.entries(_fetchServer.handlers).sort((a, b) => b[0].length - a[0].length);
  for (const [url, handler] of sortedHandlersArr) {
    newFetchServerHandlers[url] = handler;
  }
  _fetchServer.handlers = newFetchServerHandlers;
}

/**
 * Mock fetch calls according to provided map of urls<>handlers.
 * Handlers may be simple values/objects, in which case the will be returned by the associated
 * fetch call; or they may be functions, in which case they will be executed by FetchServer as:
 *
 * handler(url, params)
 *
 * @param handlers : map of urls<>handlers that will be passed to FetchServer
 * @param handleRequestErrorCallback
 *  unhandled requests errors can be caught by BaseClient or some other try/catch in async manner,
 *  that lets the test pass. Passing `(err) => this.test.callback(err)` as handleRequestErrorCallback
 * will ensure test will fail, and we'll have to ensure all fetch calls are mocked.
 */
export function mockFetch(handlers: Obj, handleRequestErrorCallback: (err: Error) => void, sortHandlers = false) {
  // revert previously-stubbed fetch if necessary to avoid
  // "Attempted to wrap fetch which is already wrapped" errors from sinon
  restoreFetch();

  // initialize FetchServer
  _fetchServer = new FetchServer(
    handlers,
    // set true to log all fetches with responses
    {debug: false},
  );

  // TODO Temp fix to allow urls with non-JSON-parseable params.body to be mocked.
  //      Remove once @junhouse has updated fetch-server to JSON.parse only when appropriate.
  function handle(url, params) {
    if (params && params.body) {
      try {
        JSON.parse(params.body);
      } catch {
        const {body, ...nonBodyParams} = params;
        params = nonBodyParams;
      }
    }

    try {
      // TODO @envp move this sort logic into Domsuite
      // This resolves an issue where there are multiple routes that will return a handler for a given URL where URL
      // should be matched with more specific route. For example,
      // URL = `/views/20/cohort/1/upsert' and we have Route1=`/views/20/cohort/1` and Route2=`/views/20/cohort/upsert`
      // If we compare URL with Route1 first, because of the Domsuite logic, we use that Route's handler instead of Route2.
      // URL.includes(Route1) => True
      // By rearranging handlers such that longest Route appears first in comparison, we temporarily mitigate this issue.
      if (sortHandlers) {
        _sortHandlers();
      }
      return _fetchServer.handle(url, params);
    } catch (err) {
      if (typeof handleRequestErrorCallback === `function`) {
        handleRequestErrorCallback(err);
      }
      // rethrow
      throw err;
    }
  }

  sinon.stub(window, `fetch`).callsFake(handle);

  // needed for FetchServer to work in NodeJS, where topmost namespace is `global`, not `window`
  // TODO incorporate into domsuite?
  // @ts-ignore
  // eslint-disable-next-line no-prototype-builtins
  if (!global?.fetch?.hasOwnProperty?.(`restore`)) {
    // @ts-ignore
    sinon.stub(global, `fetch`).callsFake(handle);
  }
}

/**
 * Attempt to restore both global.fetch and window.fetch (need both for compatibility with NodeJS).
 * Resets to original state before Sinon/FetchSever mocking.
 */
export function restoreFetch() {
  // @ts-ignore
  // eslint-disable-next-line no-prototype-builtins
  if (global?.fetch?.hasOwnProperty?.(`restore`)) {
    // @ts-ignore
    global.fetch.restore();
  }
  // @ts-ignore
  // eslint-disable-next-line no-prototype-builtins
  if (window?.fetch?.hasOwnProperty?.(`restore`)) {
    // @ts-ignore
    window.fetch.restore();
  }
}

/**
 * Replace a single FetchServer handler with a Sinon spy that may be used to introspect
 * call data about the given handler during test execution.
 * An optional handler function or value may be provided as a replacement for existing
 * handler (if non-function, will be wrapped for compatibiliy with Sinon+FetchServer)
 *
 * @param {string} url : used to identify the FetchServer handler to replace; must be exact match
 * @param {any} [newHandlerFuncOrRetValue] : replacement handler function or return value
 * @return {Sinon.spy}
 */
export function spyFetch(url: string, newHandlerFuncOrRetValue = null, sortHandlers = false) {
  newHandlerFuncOrRetValue = newHandlerFuncOrRetValue || _fetchServer.handlers[url];

  if (!isFunction(newHandlerFuncOrRetValue)) {
    const returnValue = newHandlerFuncOrRetValue;
    newHandlerFuncOrRetValue = () => returnValue;
  }

  const spy = sinon.spy(newHandlerFuncOrRetValue);
  _fetchServer.handlers[url] = spy;

  if (sortHandlers) {
    _sortHandlers();
  }

  return spy;
}
